import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { fetchReviewRewards } from '../hooks/useReviewRewards';
import { useSSE } from '../hooks/useSSE';
import { useAuth } from './AuthContext';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const FeatureFlagsContext = createContext({ flags: {}, list: [], loaded: false, refetch: async () => {} });

/**
 * Provides feature flags app-wide.
 *  - Fetches all flags whenever the authenticated user changes (login/logout)
 *  - Subscribes to SSE 'flag.updated' events
 *  - Exposes `flags[key]` boolean lookup
 */
export function FeatureFlagsProvider({ children }) {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const fetchFlags = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/feature-flags`, { withCredentials: true });
      setList(data?.flags || []);
    } catch (err) {
      // 401 (logged out) → empty list; UI falls back to defaults
      setList([]);
    } finally {
      setLoaded(true);
    }
  }, []);

  // Refetch whenever auth state changes (login/logout)
  useEffect(() => {
    fetchFlags();
  }, [fetchFlags, user?.id]);

  // React to SSE flag updates + session-expired signal.
  // Phase 6.2.3 — useSSE now emits `stream.session_expired` after the auth
  // probe confirms the cookie is dead. We surface that as a single toast so
  // users on stale tabs know to re-login (and stop wondering why nothing's
  // updating). `sessionExpiredShown` guards against double-firing if multiple
  // useSSE consumers all see it.
  const sessionExpiredShown = useRef(false);
  useSSE(useCallback((evt) => {
    if (!evt) return;
    if (evt.type === 'stream.session_expired') {
      if (sessionExpiredShown.current) return;
      sessionExpiredShown.current = true;
      toast.error('Your session expired. Please sign in again.', {
        duration: 8000,
        action: { label: 'Sign in', onClick: () => { window.location.href = '/login'; } },
      });
      return;
    }
    if (evt.type !== 'flag.updated') return;
    // Feb 2026 — Track B launch moment. When the SA flips `customer.reviews`
    // from OFF to ON, every customer currently connected to SSE sees a
    // one-time celebratory toast. localStorage guard so a refresh (or the
    // next OFF→ON toggle) doesn't spam the same customer forever.
    if (evt.key === 'customer.reviews' && evt.enabled === true && user?.role === 'customer') {
      try {
        const storageKey = `reviews_launch_toast_seen:${user?.id || 'anon'}`;
        if (!localStorage.getItem(storageKey)) {
          localStorage.setItem(storageKey, String(Date.now()));
          fetchReviewRewards().then((r) => {
            const line = r.launch
              ? `Earn ${r.pts} launch bonus points for every review during launch.`
              : `Earn ${r.pts} points for every approved review.`;
            toast.success('Product reviews are live!', {
              description: `${line} Tap any product to leave one.`,
              duration: 10000,
            });
          }).catch(() => toast.success('Product reviews are live!', { description: 'Tap any product to leave one.', duration: 10000 }));
        }
      } catch { /* private-mode localStorage → let it fire once per session */ }
    }
    setList(prev => {
      const idx = prev.findIndex(f => f.key === evt.key);
      if (idx === -1) {
        // Unknown key — refetch to reconcile
        fetchFlags();
        return prev;
      }
      const next = [...prev];
      next[idx] = { ...next[idx], enabled: evt.enabled };
      return next;
    });
  }, [fetchFlags, user?.id, user?.role]));

  const flagsMap = useMemo(() => {
    const m = {};
    for (const f of list) m[f.key] = f.enabled;
    return m;
  }, [list]);

  const value = useMemo(() => ({
    flags: flagsMap,
    list,
    loaded,
    refetch: fetchFlags,
  }), [flagsMap, list, loaded, fetchFlags]);

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

/**
 * Lookup a single flag.
 *   const showRating = useFlag('customer.rating');
 *
 * `defaultValue` is returned while flags are still loading (or for unknown keys).
 * Defaults to `true` for already-shipped features so the UI doesn't flash empty
 * during the first paint; pass `false` for planned features that should stay
 * hidden until the flag explicitly turns on.
 */
export function useFlag(key, defaultValue = true) {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx.loaded) return defaultValue;
  return ctx.flags[key] ?? defaultValue;
}

export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}
